import {
  VideoCameraIcon,
  MinusIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import CircleIcon from 'assets/CircleIcon';
import DragIcon from 'assets/DragIcon';
import DuplicateIcon from 'assets/DuplicateIcon';
import Spinner from 'assets/Spinner';
import TextIcon from 'assets/TextIcon';
import CTAModal from 'components/CTAModal/CTAModal';
import Dropdown from 'components/Dropdown/Dropdown';
import useDeleteCompetency from 'hooks/useDeleteCompetency';
import useDuplicateCompetency from 'hooks/useDuplicateCompetency';
import { CompetencyType, ICompetency } from 'hooks/useGetAssessmentSteps';
import useNotification from 'hooks/useNotification';
import { t } from 'locales/i18n';
import { AssessmentDetailsContext } from 'providers/AssessmentDetailsProvider';

interface CompetencyCardProps {
  competency: ICompetency;
  interviewUid: string;
  isDragged: boolean;
}

const CompetencyCard = ({
  competency,
  interviewUid,
  isDragged,
}: CompetencyCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { deleteCompetency, isLoading: isDeleteCompetencyLoading } =
    useDeleteCompetency();
  const { duplicateCompetency, isLoading } = useDuplicateCompetency();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const queryClient = useQueryClient();
  const { isPractice } = useContext(AssessmentDetailsContext);
  const handleDuplicateCompetency = async (includeQuestions: boolean) => {
    if (!interviewUid) return;

    await duplicateCompetency(
      {
        interviewUid,
        competencyUid: competency.uid,
        includeQuestions,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            t('Success'),
            t('Competency duplicated successfully')
          );
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(t('Error'), error.message);
        },
      }
    );
  };

  const handleDeleteCompetency = async () => {
    if (!interviewUid) return;

    await deleteCompetency(
      {
        interviewUid,
        competencyUid: competency.uid,
      },
      {
        onSuccess: () => {
          showSuccessNotification(
            isPractice ? t('Question Group Deleted') : t('Competency Deleted'),
            isPractice
              ? t('Question group successfully deleted')
              : t('Competency successfully deleted')
          );
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(t('Error'), error.message);
        },
      }
    );
  };
  const isQuestion = useMemo(() =>[
        CompetencyType.QUESTIONS,
        CompetencyType.VIDEO,
        CompetencyType.WRITTEN,
      ].includes(competency.type),[competency.type]);
  
  const icon: JSX.Element = useMemo(() => {
    switch (competency.type) {
      case CompetencyType.VIDEO:
        return <VideoCameraIcon />;
      case CompetencyType.WRITTEN:
        return <TextIcon />;
      case CompetencyType.PASSIVE:
      default:
        return <MinusIcon />;
    }
  }, [competency.type]);

  const competencyCardWrapperClassName = twMerge(
    'flex',
    'w-full',
    'items-center',
    'pl-2',
    'py-2',
    'text-left',
    'hover:bg-kira-blue-100',
    'rounded',
    'transition',
    '[&:hover>.iconWrapper]:hidden',
    '[&:hover>.dragIconWrapper]:flex',
    '[&:hover>.buttonsWrapper]:flex',
    isDragged ? 'bg-kira-blue-100' : 'bg-white'
  );

  const iconWrapperClassName = twMerge(
    'mr-2',
    'h-7',
    'w-7',
    'items-center',
    'rounded-full',
    'bg-kira-slate-100',
    'p-2',
    'text-kira-violet-900',
    isDragged ? 'hidden' : 'flex'
  );

  const dragIconWrapperClassName = twMerge(
    'mr-2',
    'h-7',
    'w-7',
    'items-center',
    'justify-center',
    'rounded',
    'text-kira-violet-900',
    isDragged ? 'flex' : 'hidden',
    'bg-transparent'
  );

  const dropdownOptions = () => {
    const options = [
      {
        label: t('Duplicate without questions'),
        onClick: () => handleDuplicateCompetency(false),
      },
    ];

    if (isQuestion) {
      options.unshift({
        label: t('Duplicate with questions'),
        onClick: () => handleDuplicateCompetency(true),
      });
    }

    return options;
  };

  return (
    <>
      <Link
        to={`/interview/${interviewUid}/steps/competency/${competency.uid}`}
        className={competencyCardWrapperClassName}
      >
        <div className={`${iconWrapperClassName} iconWrapper`}>{icon}</div>
        <div className={`${dragIconWrapperClassName} dragIconWrapper`}>
          <DragIcon pathClassName="fill-kira-blue-800" wrapperClassName="w-4" />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div
            className="prose-body-md-med flex items-center gap-2 overflow-clip truncate"
            data-testid="competencyName"
          >
            {competency.name}
          </div>
          <div
            className="prose-body-sm-reg items-center text-kira-slate-700"
            data-testid="competencyInfo"
          >
            {isQuestion && (
              <>
                {`${competency.questions} ${t('question', {
                  count: competency.questions,
                })}`}
                {!isPractice && (
                  <CircleIcon wrapperClassNames="inline-block mx-2" />
                )}
              </>
            )}

            {!isPractice && `${competency.rubric_scale} ${t('point scale')}`}
          </div>
        </div>
        {!isLoading ? (
          <div className="buttonsWrapper hidden px-2">
            <Dropdown
              options={dropdownOptions()}
              dropdownIcon={
                <DuplicateIcon
                  wrapperClassName="w-5 h-5 m-1.5"
                  pathClassName="stroke-kira-slate-700 group-hover/dropdown-button:stroke-white group-[.is-open]/dropdown-button:stroke-white"
                />
              }
              menuClassName="w-56"
              optionLabelClassName="text-kira-slate-900"
            />
            <span className="mx-0.5" />
            <Dropdown
              options={[
                {
                  label: t('Delete'),
                  icon: <TrashIcon className="h-3.5 w-3.5" />,
                  onClick: () => setIsModalOpen(true),
                },
              ]}
            />
          </div>
        ) : (
          <Spinner spinnerClassNames="w-6 h-6 text-kira-violet-900 animate-spin mx-2 " />
        )}
      </Link>

      <CTAModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        title={isPractice ? t('Delete Question Group') : t('Delete Competency')}
        description={
          isPractice
            ? t("Deleting a question group is permanent and can't be undone.")
            : t("Deleting a competency is permanent and can't be undone.")
        }
        confirmation={t('Are you sure you want to delete it?')}
        action={handleDeleteCompetency}
        isLoading={isDeleteCompetencyLoading}
        buttonDataTestId="modalDelete"
      />
    </>
  );
};

export default CompetencyCard;
